// PRD-0084 — operator-readable trust presentation for brief/output findings.
//
// PRD-0075 (Evidence Contract & Trust Gates) already produces the normalized
// provenance fields on every finding (`source_access`, `provenance_status`,
// `missing_provenance_fields`, `source_url`). This module translates those
// engineering signals into the tiny operator-facing vocabulary the report uses:
// Verified · Unverified source · System event — plus a plain-language summary and
// a relevance label (replacing the unexplained "0.85/100" score).

import type { TaskRunBriefEvidence } from "../../../shared/types";

export type FindingTrustKind = "verified" | "unverified" | "internal";

export interface FindingTrust {
  kind: FindingTrustKind;
  /** Operator-facing badge label. */
  label: string;
  /** Optional supporting detail (e.g. the verified domain). */
  detail?: string;
}

/**
 * Classify a single finding for the operator.
 *
 * - `internal`  — a system event with no external source by nature (e.g. an
 *   action-item like "Brief advanced: briefed → drafting"). MUST NOT read as
 *   "source unavailable" (PRD-0084 FR-8).
 * - `verified`  — has a usable external source URL and no missing provenance.
 * - `unverified`— search-derived but the source URL/domain/date weren't captured.
 */
export function classifyFindingTrust(finding: TaskRunBriefEvidence): FindingTrust {
  if (finding.provenance_status === "internal" || finding.source_access === "internal") {
    return { kind: "internal", label: "System event" };
  }

  const hasSourceUrl = typeof finding.source_url === "string" && finding.source_url.trim().length > 0;
  const missing = finding.missing_provenance_fields ?? [];
  const hasTrustIssues = (finding.trust_issues?.length ?? 0) > 0;

  if (hasSourceUrl && missing.length === 0 && finding.source_access !== "unavailable" && !hasTrustIssues) {
    const domain = finding.domain?.trim() || null;
    return { kind: "verified", label: "Verified", detail: domain ?? undefined };
  }

  return { kind: "unverified", label: "Unverified source" };
}

/**
 * Plain relevance label from the 0–1 finding score (FR-9). Tolerates a 0–100
 * scale defensively. Returns null when there is no usable score.
 */
export function relevanceLabel(score: number | null | undefined): "High" | "Medium" | "Low" | null {
  if (typeof score !== "number" || !Number.isFinite(score)) return null;
  const normalized = score > 1 ? score / 100 : score;
  if (normalized >= 0.7) return "High";
  if (normalized >= 0.4) return "Medium";
  if (normalized > 0) return "Low";
  return null;
}

export interface BriefTrustSummary {
  total: number;
  verified: number;
  unverified: number;
  /** System events — excluded from the source-verification tally (FR-8). */
  internal: number;
  /** Findings that should carry a source (verified + unverified). */
  sourceBacked: number;
  /** Plain-language summary for the trust banner (FR-5). */
  text: string;
  /** Whether the banner should read as a warning. */
  hasWarning: boolean;
}

/**
 * Roll the per-finding classification into the plain-language trust banner.
 * The tally is over source-backed findings only — system events never count as
 * "couldn't verify" (FR-8).
 */
export function summarizeBriefTrust(findings: TaskRunBriefEvidence[]): BriefTrustSummary {
  let verified = 0;
  let unverified = 0;
  let internal = 0;
  for (const finding of findings) {
    const kind = classifyFindingTrust(finding).kind;
    if (kind === "verified") verified += 1;
    else if (kind === "internal") internal += 1;
    else unverified += 1;
  }
  const sourceBacked = verified + unverified;

  let text: string;
  if (sourceBacked === 0) {
    text = "This brief has no source-backed findings to verify.";
  } else if (unverified === 0) {
    text = `All ${sourceBacked} source-backed finding${sourceBacked === 1 ? "" : "s"} have a verified source.`;
  } else {
    text = `${unverified} of ${sourceBacked} finding${sourceBacked === 1 ? "" : "s"} couldn't be source-verified — treat them as unconfirmed leads, not facts.`;
  }

  return {
    total: findings.length,
    verified,
    unverified,
    internal,
    sourceBacked,
    text,
    hasWarning: unverified > 0,
  };
}
